import { useEffect, useState } from 'react';
import { fetchBugs, StaleAuthenticationError } from '../../lib/api/bugs';
import { onReauthenticated } from '../../lib/api/reauthenticationSignal';
import { isActiveTicket } from './ticketStatus';

// ─────────────────────────────────────────────────────────────────────
// HOW MANY TICKETS ARE OPEN — extracted so the two floating buttons cannot
// disagree about it.
//
// `BugReportButton` owned this effect, and the DEV menu shows the same badges.
// A second copy would have been the kind of duplication that drifts silently:
// one counting closed tickets, one not, and nobody noticing because both
// numbers look plausible.
//
// ACTIVE ONLY — open and in progress. Paused and closed are not counted, which
// is what makes the badge mean «there is something to look at».
// ─────────────────────────────────────────────────────────────────────

export interface TicketCounts {
  bugs: number;
  features: number;
  total: number;
}

// ── ONE POLLER FOR THE WHOLE PAGE, AND NONE WHILE IT IS HIDDEN (#878) ──
//
// Both floating buttons used to run their own 2-second interval — the DEV
// menu through this hook, `BugReportButton` through a copy of it — and the DEV
// menu renders the bug button inside itself, so one open tab fetched the same
// list twice every two seconds. Every subscriber now shares one interval and
// one set of counts; the interval runs while somebody is subscribed.
//
// HIDDEN TABS DO NOT POLL. A background tab is somebody who is not looking,
// and it was a background tab that sent ~1.8 GB an hour. Coming back to the
// tab re-reads at once, so the badge is never older than it was before.
//
// The 2-second cadence itself is unchanged: lengthening it is a product call.

export const TICKET_POLL_MS = 2000;

const ZERO: TicketCounts = { bugs: 0, features: 0, total: 0 };
let current: TicketCounts = ZERO;
const listeners = new Set<(c: TicketCounts) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let generation = 0;

const isHidden = () => typeof document !== 'undefined' && document.hidden === true;

async function load(): Promise<void> {
  const gen = generation;
  try {
    const all = await fetchBugs();
    // Everybody unsubscribed meanwhile: a late answer must not resurrect counts.
    if (gen !== generation || listeners.size === 0) return;
    const active = all.length ? all.filter(isActiveTicket) : [];
    const bugs = active.filter((b: any) => (b.kind || 'bug') === 'bug').length;
    const features = active.filter((b: any) => b.kind === 'feature').length;
    current = { bugs, features, total: bugs + features };
    for (const l of listeners) l(current);
  } catch (e) {
    // TOO OLD A SESSION (#1141): stop asking. Polling on would re-open the
    // step-up prompt two seconds after every dismissal. The last count stands
    // until the person proves themselves again, or comes back to the tab.
    if (e instanceof StaleAuthenticationError) {
      paused = true;
      stopTimer();
    }
    /* otherwise ignore — the count is best-effort, and the last one stands */
  }
}

/** Polling stopped because the session was too old (#1141). */
let paused = false;

onReauthenticated(() => {
  if (!paused) return;
  paused = false;
  if (listeners.size === 0 || isHidden()) return;
  void load();
  startTimer();
});

function startTimer() {
  if (timer == null && !isHidden()) timer = setInterval(() => void load(), TICKET_POLL_MS);
}
function stopTimer() {
  if (timer != null) clearInterval(timer);
  timer = null;
}

function onVisibility() {
  if (isHidden()) {
    stopTimer();
  } else if (timer == null) {
    void load();
    startTimer();
  }
}

/**
 * A re-read NOW, and the interval restarted from it — what a changed
 * `refreshKey` used to do. Coalesced per tick: the DEV menu and the bug button
 * inside it mount (and close their modals) in the same commit, and that is one
 * read, not two.
 */
let refreshQueued = false;
function refresh() {
  if (refreshQueued) return;
  refreshQueued = true;
  queueMicrotask(() => {
    refreshQueued = false;
    stopTimer();
    if (listeners.size === 0 || isHidden()) return;
    void load();
    startTimer();
  });
}

function subscribe(listener: (c: TicketCounts) => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    stopTimer();
    generation += 1;
    current = ZERO; // a fresh mount starts from zero, as it always did
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}

/**
 * Poll the ticket counts.
 *
 * `refreshKey` re-reads immediately when it changes — the buttons pass their
 * modal's open state, so closing the report form shows the new ticket at once
 * rather than up to two seconds later.
 *
 * The 2-second poll is inherited behaviour, and it is what keeps the badge
 * honest while somebody deletes or closes tickets in the archive on another
 * tab. Best-effort throughout: a failed read leaves the last known counts
 * rather than flashing zero, because a zero badge says «nothing to look at»,
 * which is a stronger claim than «I could not ask».
 *
 * Shared: however many components call this, the page runs one poller (see
 * above), and it pauses while the tab is hidden.
 *
 * `enabled` false — a caller who is not a bug administrator — subscribes to
 * nothing, starts no poller and answers zero.
 */
export function useTicketCounts(refreshKey?: unknown, enabled = true): TicketCounts {
  const [counts, setCounts] = useState<TicketCounts>(enabled ? current : ZERO);

  // ONLY FOR SOMEBODY WHO MAY READ THE LIST (owner decision 2026-10-22). A
  // reporter is submit-only: no counts, and no poll — the list endpoint would
  // refuse them every two seconds, which is load for nothing and a failed
  // request in every bug report's trail.
  useEffect(() => {
    if (!enabled) {
      setCounts(ZERO);
      return undefined;
    }
    return subscribe(setCounts);
  }, [enabled]);
  useEffect(() => {
    if (enabled) refresh();
  }, [refreshKey, enabled]);

  return enabled ? counts : ZERO;
}
